'use client';

import { useContext, useRef, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams, usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';

import { LoginModalContext } from 'providers/LoginModalProvider';

import { useGetCustomFingerprint } from './useGetCustomFingerprint';

// reducers
import { getUser } from 'reducers/user/apiFunctions';
import { setError } from 'reducers/partnerVerification/slice';

// utils
import { trackEvent } from 'utils/analytics';
import { getAPI } from 'utils/helpers';

/**
 * Stub element returned by `renderLoginModal()` to preserve the historical
 * call-site shape (`{renderLoginModal({...})}` placed inline in JSX) without
 * actually mounting a second `<LoginModal>` next to the one in
 * `LoginModalProvider`.
 *
 * The stub renders nothing visible. It exists to expose the wired-up handler
 * props (`onError`, `onClose`, `onSuccessfulLogin`, …) on the returned React
 * element so existing tests can introspect them via `view.props.onError` —
 * matching the pre-PD-34298 API exactly.
 */
const LoginModalConfigStub = () => null;

/**
 * Hook that gives a component the ability to open / close the single shared
 * login modal mounted by `LoginModalProvider`.
 *
 * Returns a 3-tuple kept identical to the pre-PD-34298 API so the 25+ existing
 * call sites do not need to change:
 *
 *   const [renderLoginModal, setLoginModalOpen, isOpen] = useLoginModal();
 *
 * Usage pattern (unchanged for callers):
 *
 *   const handleClick = () => {
 *     pendingActionRef.current = { type: 'foo' };
 *     setLoginModalOpen(true);
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={handleClick}>Log In</button>
 *       {renderLoginModal({
 *         offering: offeringData,
 *         onSuccessfulLogin: (resp) => { ... },
 *       })}
 *     </>
 *   );
 *
 * What changed under the hood: `renderLoginModal()` no longer returns an
 * actual `<LoginModal>`. It registers the caller-supplied config with this
 * hook instance (latest config wins per call site) and returns a no-op stub
 * element. When `setLoginModalOpen(true)` is called, the hook hands its
 * registered config to the provider, which opens the single shared modal.
 *
 * Why preserve the inline-`renderLoginModal()` API at all (instead of just
 * exporting `openLogin(config)`)? Because changing 25+ call sites in the
 * same PR multiplies review surface and regression risk. The stub-element
 * trick keeps the diff tiny while still eliminating the multi-instance
 * scroll-lock pile-on described in `LoginModalProvider`.
 */
// Fallback context value used when the hook is invoked outside of a
// `<LoginModalProvider>` ancestor (e.g. in older tests that render a single
// component directly without the provider stack). Production always has the
// provider mounted in ProviderWrapper, so this path is purely a safety net
// for narrow unit tests that don't exercise login behavior.
const FALLBACK_CTX = {
  openLoginModal: () => {},
  closeLoginModal: () => {},
  isOpen: false,
};

let warnedMissingProvider = false;

const useLoginModal = () => {
  const ctxFromProvider = useContext(LoginModalContext);
  if (!ctxFromProvider && process.env.NODE_ENV !== 'production' && !warnedMissingProvider) {
    warnedMissingProvider = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[useLoginModal] No <LoginModalProvider> ancestor found. Using a no-op fallback. Production mounts the provider in ProviderWrapper; in tests, wrap your render with <LoginModalProvider> (or use testUtils/MockWrapper) if you need login behavior.',
    );
  }
  const { openLoginModal, closeLoginModal, isOpen } = ctxFromProvider || FALLBACK_CTX;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { fingerprint } = useGetCustomFingerprint();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchParamConnectRequestId = searchParams.get('connectRequestId');
  const connectRequestId = searchParamConnectRequestId?.replaceAll('/', '');

  // Determine country based on route - Canadian pages should set country to CA
  const isCanadianRoute = pathname?.startsWith('/private-canada');
  const country = isCanadianRoute ? 'CA' : null;
  const phoneCountry = isCanadianRoute ? 'ca' : undefined;

  // Latest config registered by THIS hook instance via renderLoginModal().
  // Used when the same instance later calls setLoginModalOpen(true) so the
  // provider opens with the right offering / handlers / subtitle.
  const lastConfigRef = useRef(null);

  // PD-34298 follow-up (Devin): pending-open signal driven via state +
  // useEffect rather than reading lastConfigRef synchronously. This is the
  // ONLY reliable way to ensure we open with the latest registered config
  // when callers do `setSomeState(value); setLoginModalOpen(true);` in the
  // same event handler — see the lengthy comment on `setLoginModalOpen`
  // below for why microtask defers don't work in batched-render mode.
  const [pendingOpenSignal, setPendingOpenSignal] = useState(0);
  useEffect(() => {
    if (pendingOpenSignal > 0) {
      // useEffect runs AFTER React commits, by which point `renderLoginModal`
      // has been called for the current render and `lastConfigRef.current`
      // reflects the latest closure (with the just-committed state values).
      openLoginModal(lastConfigRef.current || {});
    }
    // Intentionally omit openLoginModal from deps — the provider re-creates
    // it on every isOpen change, which would re-fire this effect spuriously.
    // openLoginModal's identity is stable enough for our purposes (the
    // closure inside captures `setRenderedConfig` + `setIsOpen`, both stable
    // setState refs from useState).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenSignal]);

  const renderLoginModal = ({
    offering,
    verifiedPrimaryUserId,
    onClose,
    onSuccessfulLogin,
    subtitle,
  } = {}) => {
    const handleCloseModal = () => {
      closeLoginModal();
      trackEvent('closed_login_signup_modal');

      if (onClose) {
        onClose();
      }
    };

    const handleLoginError = (errorType, error) => {
      if (errorType === 'login' && error?.response?.status === 400) {
        const errorMessage = error?.response?.data?.message || error?.response?.data;

        if (
          typeof errorMessage === 'string' &&
          errorMessage.toLowerCase().includes('account deactivated')
        ) {
          dispatch(
            setError('Your account has been deactivated. Please contact support for assistance.'),
          );
        }
      }
    };

    const handleSuccessfulLogin = async (response) => {
      closeLoginModal();
      trackEvent('closed_login_signup_modal');

      if (
        response?.primaryUserId &&
        verifiedPrimaryUserId &&
        response.primaryUserId !== verifiedPrimaryUserId &&
        onSuccessfulLogin
      ) {
        dispatch(
          setError(
            'It looks like this user does not match our records for this connect request, please try again.',
          ),
        );
        return onSuccessfulLogin({ error: true });
      }

      queryClient.invalidateQueries({ queryKey: ['user'] });
      // Clips responses are per-user once authenticated (e.g. likedByMe), so
      // refetch them with the fresh JWT. Logout needs no equivalent — it does
      // a full page navigation, which drops the in-memory query cache.
      queryClient.invalidateQueries({ queryKey: ['clips'] });
      queryClient.invalidateQueries({ queryKey: ['clip'] });

      await dispatch(
        getUser({
          getInvestmentAccount: true,
          skipRedirect: false,
        }),
      );

      if (onSuccessfulLogin) {
        onSuccessfulLogin(response);
      }

      return null;
    };

    const wiredConfig = {
      fingerprint,
      offering,
      subtitle,
      connectRequestId,
      gatewayUrl: getAPI(),
      country,
      phoneCountry,
      turnstileSiteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      disableScrollLock: true,
      onClose: handleCloseModal,
      onError: handleLoginError,
      onSuccessfulLogin: handleSuccessfulLogin,
    };

    lastConfigRef.current = wiredConfig;

    return <LoginModalConfigStub {...wiredConfig} />;
  };

  const setLoginModalOpen = useCallback(
    (shouldOpen) => {
      if (!shouldOpen) {
        closeLoginModal();
        return;
      }

      // PD-34298 follow-up (Devin): callers commonly do
      //   setSomeState(value);
      //   setLoginModalOpen(true);
      // in the same event handler (e.g. BuilderHeroSection updates
      // `redirectUrl` then opens the modal). React 18 batches both state
      // updates, but if we synchronously read `lastConfigRef.current` here
      // (or even via queueMicrotask), we read the PREVIOUS render's
      // handlers — they close over the OLD state (e.g. `redirectUrl=''`).
      // The provider would then freeze those stale handlers into its
      // render config and the modal's `onSuccessfulLogin` would redirect
      // to the wrong URL.
      //
      // queueMicrotask is NOT enough: in React 18's batched-render mode,
      // microtasks are processed before React's render+commit phases
      // (those use the scheduler package, which uses MessageChannel /
      // setTimeout — strictly later than microtasks). So a microtask still
      // reads the pre-flush ref.
      //
      // The reliable defer is via state + useEffect: bumping the pending-
      // open signal triggers a re-render of THIS component, the new render
      // calls `renderLoginModal` (writing the FRESH config to the ref),
      // commit happens, and the effect above fires AFTER commit — by which
      // time the ref reflects the latest closure. The cost is one extra
      // re-render of the caller component; trivial compared to the
      // correctness benefit.
      setPendingOpenSignal((s) => s + 1);
    },
    [closeLoginModal],
  );

  return [renderLoginModal, setLoginModalOpen, isOpen];
};

export default useLoginModal;
